import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { Action } from '@/services/auth/capabilities';

// Client-side capability overrides for the dev panel's Access tab. Lets an admin
// preview the UI as if a capability were granted or denied, without a real token
// change. Purely cosmetic — the backend always enforces the genuine roles, and
// useCan only honours these for users who really hold admin.dev-panel.
interface DevOverridesState {
  overrides: Partial<Record<Action, boolean>>;
}

interface DevOverridesActions {
  // value === undefined clears the override (back to policy default).
  setOverride: (action: Action, value: boolean | undefined) => void;
  reset: () => void;
}

type DevOverridesStore = DevOverridesState & DevOverridesActions;

export const useDevOverridesStore = create<DevOverridesStore>()(
  devtools(
    persist(
      (set) => ({
        overrides: {},
        setOverride: (action, value) =>
          set(
            (s) => {
              const next = { ...s.overrides };
              if (value === undefined) delete next[action];
              else next[action] = value;
              return { overrides: next };
            },
            false,
            'setOverride'
          ),
        reset: () => set({ overrides: {} }, false, 'reset'),
      }),
      { name: 'dev-capability-overrides' }
    ),
    { name: 'DevOverridesStore', enabled: import.meta.env.DEV }
  )
);

/** Single-action override lookup for useCan; undefined = no override in effect. */
export const useDevOverride = (action: Action): boolean | undefined =>
  useDevOverridesStore((s) => s.overrides[action]);

export const useDevOverrides = () => useDevOverridesStore((s) => s.overrides);
